import { AnimatePresence, motion } from "framer-motion";
import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { EASE_OUT } from "../theme";

interface AccordionGroupProps {
  id: string;
  label: string;
  icon: string;
  open: boolean;
  onToggle: (id: string) => void;
  badge?: ReactNode;
  children: ReactNode;
}

/** One collapsible right-rail section (StoneAlgo-style: Metal / Diamonds /
 * Head / Band / More). Several groups can be open at once. */
export function AccordionGroup({ id, label, icon, open, onToggle, badge, children }: AccordionGroupProps) {
  return (
    <div className="border-b border-outline-variant/30">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="flex w-full items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-surface-container-high/20"
      >
        <Icon name={icon} size={18} className="text-secondary" />
        <span className="flex-1 font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-primary">
          {label}
        </span>
        {badge}
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2, ease: EASE_OUT }}>
          <Icon name="expand_more" size={18} className="text-on-surface-variant" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-5 px-6 pb-6">{children}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** Small uppercase pill used to flag not-yet-wired option groups. */
export function ComingSoonTag() {
  return (
    <span className="rounded-full border border-outline-variant/50 bg-surface-container-high/40 px-2 py-0.5 font-display text-[9px] font-semibold uppercase tracking-[0.1em] text-outline">
      Next Wave
    </span>
  );
}
